// ============================================
// components/autoProgression.js
// Auto-progression system for missions
// ============================================

const DEPLOYMENT_STATUS = {
  ON_MISSION: 'On Mission',
  ACTIVE: 'Active',
  COMPLETED: 'Completed',
};

/**
 * Progress all active missions by one day
 * Call this when advancing to the next day
 */
export const progressMissions = (activeMissions) => {
  if (!Array.isArray(activeMissions)) return { updated: [], completed: [] };

  const updated = [];
  const completed = [];

  activeMissions.forEach(mission => {
    // Skip missions that aren't active
    if (mission.status !== DEPLOYMENT_STATUS.ACTIVE) {
      updated.push(mission);
      return;
    }

    const newDaysRemaining = mission.days_remaining - 1;

    if (newDaysRemaining <= 0) {
      // Mission completed - resolve it
      const resolvedMission = resolveMission(mission);
      completed.push(resolvedMission);
    } else {
      // Mission still in progress
      updated.push({
        ...mission,
        days_remaining: newDaysRemaining,
      });
    }
  });

  return { updated, completed };
};

/**
 * Resolve a completed mission (success/failure + rewards/casualties)
 */
export const resolveMission = (mission) => {
  // Roll for success
  const successRoll = Math.random() * 100;
  const success = successRoll <= (mission.risks?.successRate || 80);

  // Calculate casualties
  const casualties = calculateCasualties(mission, success);
  
  // Calculate rewards (reduced if failed)
  const rewards = calculateRewards(mission, success);

  return {
    ...mission,
    status: DEPLOYMENT_STATUS.COMPLETED,
    success,
    casualties,
    rewards,
    days_remaining: 0,
    completed_on_day: mission.deployed_on_day + mission.days_remaining,
  };
};

/**
 * Calculate casualties based on mission risk and outcome
 */
const calculateCasualties = (mission, success) => {
  const casualties = [];
  
  if (!mission.squad || !Array.isArray(mission.squad)) return casualties;

  // Get casualty risk level
  const riskLevel = getRiskLevel(mission.risks?.casualty);
  
  // Calculate casualty chance (higher if failed)
  let casualtyChance = riskLevel.chance;
  if (!success) {
    casualtyChance = Math.min(casualtyChance * 2, 80); // Double chance on failure, max 80%
  }

  mission.squad.forEach(soldier => {
    const roll = Math.random() * 100;
    
    if (roll < casualtyChance) {
      // Soldier casualty - calculate damage
      const damage = Math.floor(Math.random() * (riskLevel.maxDamage - riskLevel.minDamage + 1)) + riskLevel.minDamage;
      
      casualties.push({
        soldierId: soldier.id,
        soldierName: soldier.name,
        damage,
        killed: (soldier.health - damage) <= 0,
      });
    }
  });

  return casualties;
};

/**
 * Get risk level stats from risk string
 */
const getRiskLevel = (riskString) => {
  const risk = (riskString || 'medium').toLowerCase();
  
  const levels = {
    low: { chance: 10, minDamage: 5, maxDamage: 20 },
    medium: { chance: 25, minDamage: 15, maxDamage: 40 },
    moderate: { chance: 25, minDamage: 15, maxDamage: 40 },
    high: { chance: 40, minDamage: 25, maxDamage: 60 },
  };

  return levels[risk] || levels.medium;
};

/**
 * Calculate actual rewards (reduced on failure)
 */
const calculateRewards = (mission, success) => {
  if (!mission.rewards) return {};

  const multiplier = success ? 1 : 0.5; // 50% rewards on failure

  return {
    funds: Math.floor((mission.rewards.funds || 0) * multiplier),
    intel: Math.floor((mission.rewards.intel || 0) * multiplier),
    supplies: Math.floor((mission.rewards.supplies || 0) * multiplier),
    xp: Math.floor((mission.rewards.xp || 0) * multiplier),
  };
};

/**
 * Apply mission results to squad members
 * Returns updated squad array
 */
export const applyMissionResults = (squad, completedMission) => {
  if (!Array.isArray(squad) || !completedMission) return squad;

  const casualties = completedMission.casualties || [];
  const casualtyMap = new Map(casualties.map(c => [c.soldierId, c]));

  return squad.map(soldier => {
    // Check if this soldier was on the mission
    const wasOnMission = completedMission.squad?.some(s => s.id === soldier.id);
    
    if (!wasOnMission) return soldier;

    // Check if soldier was a casualty
    const casualty = casualtyMap.get(soldier.id);
    
    if (casualty) {
      return {
        ...soldier,
        health: Math.max(0, soldier.health - casualty.damage),
        status: casualty.killed ? 'KIA' : 'Active',
      };
    }

    // Soldier survived unharmed - add XP and return to active
    return {
      ...soldier,
      status: 'Active',
      xp: (soldier.xp || 0) + Math.floor((completedMission.rewards?.xp || 0) / completedMission.squad.length),
    };
  });
};

/**
 * Apply resource rewards from completed mission
 */
export const applyMissionRewards = (currentResources, completedMission) => {
  if (!completedMission.rewards) return currentResources;

  return {
    ...currentResources,
    funds: (currentResources.funds || 0) + (completedMission.rewards.funds || 0),
    intel: (currentResources.intel || 0) + (completedMission.rewards.intel || 0),
    supplies: (currentResources.supplies || 0) + (completedMission.rewards.supplies || 0),
  };
};

/**
 * Main function to call when advancing day
 * Returns all updates needed
 */
export const advanceDayWithMissions = (gameState) => {
  const { activeMissions, squad, resources } = gameState;

  // Progress missions
  const { updated: ongoingMissions, completed: completedMissions } = progressMissions(activeMissions);

  // Apply results
  let updatedSquad = squad;
  let updatedResources = resources;

  completedMissions.forEach(mission => {
    updatedSquad = applyMissionResults(updatedSquad, mission);
    updatedResources = applyMissionRewards(updatedResources, mission);
  });

  return {
    activeMissions: ongoingMissions,
    completedMissions, // Keep for showing results to player
    squad: updatedSquad,
    resources: updatedResources,
  };
};